import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';

export interface SessionState {
  isIdle: boolean;
  isTimedOut: boolean;
  countdown: number;
  absoluteSessionEnd: Date | null;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private sessionState$ = new Subject<SessionState>();
  private warningShown = false;
  private absoluteSessionTimer: any = null;
  private loginTime: Date | null = null;
  private broadcastChannel: BroadcastChannel | null = null;

  // Observable for components to subscribe to session state changes
  public sessionStateChanges = this.sessionState$.asObservable();

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router
  ) {
    // Only initialize if session timeout is enabled for MPH environment
    if (this.isSessionTimeoutEnabled) {
      this.initializeSessionTimeout();
    }
  }

  /**
   * Check if session timeout is enabled (MPH environment only)
   */
  get isSessionTimeoutEnabled(): boolean {
    return environment.features?.specialtyIntakeEnabled &&
           environment.session?.enabled;
  }

  /**
   * Initialize session timeout functionality
   */
  private initializeSessionTimeout(): void {
    // Set idle timeout from config (in seconds)
    const idleTimeSeconds = (environment.session?.idleTimeoutMinutes || 60) * 60;
    this.idle.setIdle(idleTimeSeconds);

    // Set warning timeout (2 minutes before idle timeout)
    const warningSeconds = (environment.session?.warningBeforeTimeoutMinutes || 2) * 60;
    this.idle.setTimeout(warningSeconds);

    // Set interrupts (user activity events that reset the idle timer)
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Handle idle start (user has been inactive for the idle period)
    this.idle.onIdleStart.subscribe(() => {
      this.handleIdleStart();
    });

    // Handle idle end (user became active again)
    this.idle.onIdleEnd.subscribe(() => {
      this.handleIdleEnd();
    });

    // Handle timeout warning countdown
    this.idle.onTimeoutWarning.subscribe((countdown: number) => {
      this.handleTimeoutWarning(countdown);
    });

    // Handle timeout (user will be logged out)
    this.idle.onTimeout.subscribe(() => {
      this.handleTimeout();
    });

    // Configure keepalive pings (optional - can be used to keep server session alive)
    this.keepalive.interval(15 * 60); // Ping every 15 minutes
    this.keepalive.onPing.subscribe(() => {
      // Could ping server to keep session alive if needed
    });

    // Initialize BroadcastChannel for multi-tab activity sync
    // Note: BroadcastChannel is not available in incognito/private browsing
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel('session_activity');
        this.broadcastChannel.onmessage = (event) => {
          if (event.data.type === 'activity') {
            // Reset idle timer when activity detected in another tab
            this.resetIdleTimer();
          } else if (event.data.type === 'logout') {
            // Logout in this tab when logout occurs in another tab
            this.performLogout('Multi-tab logout');
          }
        };
      } catch (error) {
        this.broadcastChannel = null;
      }
    }

    // Broadcast activity on user interaction
    this.idle.onInterrupt.subscribe(() => {
      this.broadcastActivity();
    });
  }

  /**
   * Start session monitoring (call after successful login)
   */
  startSession(rememberMe: boolean = false): void {
    if (!this.isSessionTimeoutEnabled) {
      return;
    }

    this.loginTime = new Date();
    this.warningShown = false;

    // Start idle watching
    this.idle.watch();

    // Set absolute session timeout
    const absoluteTimeoutHours = rememberMe
      ? (environment.session?.rememberMeTimeoutDays || 30) * 24
      : (environment.session?.absoluteTimeoutHours || 12);

    const absoluteTimeoutMs = absoluteTimeoutHours * 60 * 60 * 1000;
    const expiryDate = new Date(Date.now() + absoluteTimeoutMs);

    // Clear existing timer if any
    if (this.absoluteSessionTimer) {
      clearTimeout(this.absoluteSessionTimer);
    }

    // Set new absolute timeout
    this.absoluteSessionTimer = setTimeout(() => {
      this.handleAbsoluteTimeout();
    }, absoluteTimeoutMs);

    // Store session info in localStorage for multi-tab sync
    // Use try-catch for incognito mode compatibility
    try {
      const sessionInfo = {
        loginTime: this.loginTime.toISOString(),
        absoluteExpiry: new Date(Date.now() + absoluteTimeoutMs).toISOString(),
        rememberMe
      };
      localStorage.setItem('mph_session_info', JSON.stringify(sessionInfo));
    } catch (error) {
      console.warn('Failed to store session info in localStorage (incognito mode?):', error);
      // Session timeout will still work via in-memory timer, just without multi-tab sync
    }
  }

  /**
   * Stop session monitoring (call on logout)
   */
  stopSession(): void {
    if (!this.isSessionTimeoutEnabled) {
      return;
    }

    this.idle.stop();

    if (this.absoluteSessionTimer) {
      clearTimeout(this.absoluteSessionTimer);
      this.absoluteSessionTimer = null;
    }

    this.loginTime = null;
    this.warningShown = false;

    // Remove session info from localStorage
    try {
      localStorage.removeItem('mph_session_info');
    } catch (error) {
      console.warn('Failed to remove session info from localStorage:', error);
    }

    // Broadcast logout to other tabs
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage({ type: 'logout' });
      } catch (error) {
        console.warn('Failed to broadcast logout:', error);
      }
    }
  }

  /**
   * Reset idle timer (call on user activity or API calls)
   */
  resetIdleTimer(): void {
    if (!this.isSessionTimeoutEnabled) return;

    // Check if absolute session has expired
    // Try localStorage first, then fallback to in-memory loginTime (for incognito mode)
    const sessionInfo = this.getSessionInfo();
    if (sessionInfo && sessionInfo.absoluteExpiry) {
      const now = new Date();
      const expiry = new Date(sessionInfo.absoluteExpiry);
      const remainingMs = expiry.getTime() - now.getTime();
      if (now >= expiry) {
        this.handleAbsoluteTimeout();
        return;
      }
    } else if (this.loginTime && this.absoluteSessionTimer === null) {
      // If localStorage failed but we have loginTime, session already timed out
      this.handleAbsoluteTimeout();
      return;
    }

    // Reset idle timer
    this.idle.watch();
    this.warningShown = false;
  }

  /**
   * Continue session (user clicked "Continue" on warning modal)
   */
  continueSession(): void {
    if (!this.isSessionTimeoutEnabled) return;

    this.resetIdleTimer();
    this.warningShown = false;

    this.emitSessionState({
      isIdle: false,
      isTimedOut: false,
      countdown: 0,
      absoluteSessionEnd: this.getAbsoluteSessionEnd()
    });
  }

  /**
   * Get session info from localStorage
   * Returns null if localStorage is unavailable (incognito mode) or parsing fails
   */
  private getSessionInfo(): any {
    try {
      const sessionInfoStr = localStorage.getItem('mph_session_info');
      if (sessionInfoStr) {
        try {
          return JSON.parse(sessionInfoStr);
        } catch (e) {
          console.warn('Failed to parse session info:', e);
          return null;
        }
      }
      return null;
    } catch (error) {
      // localStorage access may fail in incognito mode
      console.warn('Failed to access localStorage:', error);
      return null;
    }
  }

  /**
   * Get absolute session end time
   */
  private getAbsoluteSessionEnd(): Date | null {
    const sessionInfo = this.getSessionInfo();
    if (sessionInfo && sessionInfo.absoluteExpiry) {
      return new Date(sessionInfo.absoluteExpiry);
    }
    return null;
  }

  /**
   * Broadcast activity to other tabs
   */
  private broadcastActivity(): void {
    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.postMessage({ type: 'activity' });
      } catch (error) {
        console.warn('Failed to broadcast activity:', error);
      }
    }
  }

  /**
   * Handle idle start
   */
  private handleIdleStart(): void {
    this.warningShown = true;

    this.emitSessionState({
      isIdle: true,
      isTimedOut: false,
      countdown: (environment.session?.warningBeforeTimeoutMinutes || 2) * 60,
      absoluteSessionEnd: this.getAbsoluteSessionEnd()
    });
  }

  /**
   * Handle idle end
   */
  private handleIdleEnd(): void {
    this.warningShown = false;

    this.emitSessionState({
      isIdle: false,
      isTimedOut: false,
      countdown: 0,
      absoluteSessionEnd: this.getAbsoluteSessionEnd()
    });
  }

  /**
   * Handle timeout warning countdown
   */
  private handleTimeoutWarning(countdown: number): void {
    this.emitSessionState({
      isIdle: true,
      isTimedOut: false,
      countdown,
      absoluteSessionEnd: this.getAbsoluteSessionEnd()
    });
  }

  /**
   * Handle idle timeout
   */
  private handleTimeout(): void {
    this.performLogout('Your session has expired due to inactivity');
  }

  /**
   * Handle absolute timeout
   */
  private handleAbsoluteTimeout(): void {
    this.performLogout('Your session has expired');
  }

  /**
   * Perform logout and redirect to login page
   * For SSO users, goes through SAML logout to clear Microsoft session
   * and prevent auto-login loop
   */
  private performLogout(message: string): void {
    this.emitSessionState({
      isIdle: false,
      isTimedOut: true,
      countdown: 0,
      absoluteSessionEnd: null
    });

    // Stop session monitoring
    this.stopSession();

    // Capture user info and token before clearing storage
    const userId = localStorage.getItem('endUserId') || '';
    const userType = localStorage.getItem('endUserType') || '';
    const token = localStorage.getItem('accessToken') || '';

    // Clear storage
    localStorage.clear();
    sessionStorage.clear();

    // Store logout message for display on login page (after clear so it persists)
    sessionStorage.setItem('session_timeout_message', message);

    if (environment.features?.specialtyIntakeEnabled) {
      const serverUrl = window.location.hostname.indexOf('localhost') > -1
        ? 'http://localhost:8080'
        : window.location.origin;

      // Revoke JWT on backend (fire and forget — don't block redirect)
      if (token) {
        fetch(`${serverUrl}/api/user/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {});
      }

      // Only VC/PCV users use SSO — redirect through Microsoft logout
      // Admin and specialist use email/password — redirect to their sign-in page
      if (userType === 'vc' || userType === 'pcv') {
        const logoutUrl = userId
          ? `${serverUrl}/api/auth/saml/logout?userId=${userId}`
          : `${serverUrl}/api/auth/saml/logout`;
        window.location.href = logoutUrl;
      } else if (userType === 'specialist') {
        window.location.href = '/auth/specialist-signin';
      } else if (userType === 'admin') {
        window.location.href = '/auth/admin-signin';
      } else {
        window.location.href = '/';
      }
    } else {
      // Legacy environment: just redirect to login page
      this.router.navigate(['/auth/login']);
    }
  }

  /**
   * Emit session state to subscribers
   */
  private emitSessionState(state: SessionState): void {
    this.sessionState$.next(state);
  }

  /**
   * Check if session is currently idle
   */
  isIdle(): boolean {
    return this.idle.isIdling();
  }

  /**
   * Get remaining time until absolute timeout (in milliseconds)
   */
  getRemainingTime(): number {
    const sessionInfo = this.getSessionInfo();
    if (sessionInfo && sessionInfo.absoluteExpiry) {
      const now = new Date();
      const expiry = new Date(sessionInfo.absoluteExpiry);
      return expiry.getTime() - now.getTime();
    }
    return 0;
  }

  /**
   * Clean up on service destroy
   */
  ngOnDestroy(): void {
    this.stopSession();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
    }
  }
}
